// Filename: src/components/Analyzers/IeepaInfoModal.tsx
import React from 'react';
// MUI Icons
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface IeepaInfoModalProps {
  open: boolean;
  handleClose: () => void;
}

const IeepaInfoModal: React.FC<IeepaInfoModalProps> = ({
  open,
  handleClose,
}) => {
  if (!open) return null;

  // Updated to use standard CSS variables for theme adapting
  const listStyles = {
    margin: '0 0 0 20px',
    lineHeight: '1.6',
    padding: 0,
    fontSize: '13px',
    color: 'var(--text-secondary)',
  };

  const listItemStyles = {
    marginBottom: '8px',
  };

  const labelStyles = {
    color: 'var(--text-primary-color)',
    fontWeight: 600,
  };

  const sectionTitleStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text-primary-color)',
    marginBottom: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const cardStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--es-soft-border)',
    borderRadius: '10px',
    padding: '18px',
    marginTop: '16px',
    marginBottom: '16px',
  };

  return (
    <div
      className='modal-overlay'
      onClick={handleClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1300,
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bg-primary)', // Ensures modal body adapts properly
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--es-soft-border)',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{
                  fontSize: '20px',
                  marginBottom: '4px',
                  color: 'var(--text-primary-color)',
                }}
              >
                Preparing Your ACE Document for IEEPA Refund Generation
              </h1>
              <p
                className='modal-header-cont-text-p'
                style={{ margin: 0, color: 'var(--text-secondary)' }}
              >
                Learn the column requirements and rules for generating an IEEPA
                report.
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={handleClose}
              style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body ieepa-info-modal-scroll'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
        >
          <p
            style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              margin: 0,
              lineHeight: '1.5',
            }}
          >
            To properly generate an IEEPA report, your uploaded ACE document
            must contain specific fields to calculate eligibility for CAPE Phase
            I based on payment, filing windows, and various control statuses.{' '}
            <strong style={{ color: 'var(--text-primary-color)' }}>
              Importantly, a transaction must contain duty paid for a
              country-specific IEEPA tariff or a reciprocal IEEPA tariff.
            </strong>{' '}
            Synthesized below are the required columns derived from IEEPA rules.
          </p>

          {/* Rules-Based Filtering Requirements Card */}
          <div style={cardStyle}>
            <div style={sectionTitleStyles}>
              <FilterListIcon sx={{ fontSize: 18, color: '#4895ef' }} />
              Rules-Based Filtering
            </div>
            <p style={{ ...listStyles, marginLeft: 0, marginBottom: '12px' }}>
              The following columns and values are required to determine
              eligibility based on synthesized rules:
            </p>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Goods Value Rule:</strong> Ensure{' '}
                <strong style={labelStyles}>
                  Line Tariff Goods Value Amount
                </strong>{' '}
                is zero or blank on IEEPA Chapter 99 ordinal lines.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Filing Window:</strong> Requires{' '}
                <strong style={labelStyles}>Liquidation Date</strong> to
                determine if filed after 04/20/2026.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Entry Summary Status:</strong> Must
                be Accepted. Requires{' '}
                <strong style={labelStyles}>Entry Summary Status</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Control Status:</strong> Must be
                CBP. Requires{' '}
                <strong style={labelStyles}>Control Status</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Drawback & Reconciliation:</strong>{' '}
                Indicator must be No. Requires{' '}
                <strong style={labelStyles}>Drawback Claimed Indicator</strong>{' '}
                and{' '}
                <strong style={labelStyles}>Reconciliation Indicator</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Protest Status:</strong> Must be
                blank. Requires{' '}
                <strong style={labelStyles}>Protest Status</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Entry Type:</strong> Must be one of
                Acceptable Values (01, 11, or 06). Requires{' '}
                <strong style={labelStyles}>Entry Type</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Liquidation Status:</strong> Must be
                one of Acceptable Values (Liquidated, Unliquidated, Extended,
                Suspended, or Under review). Requires{' '}
                <strong style={labelStyles}>Liquidation Status</strong>.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>AD/CVD Exclusion:</strong>{' '}
                Antidumping and Countervailing Duty Amounts must both be zero.
                Requires{' '}
                <strong style={labelStyles}>Antidumping Duty Amount</strong> and{' '}
                <strong style={labelStyles}>Countervailing Duty Amount</strong>.
              </li>
            </ul>
          </div>

          {/* CAPE Phase II — Claim Eligibility Card */}
          <div style={cardStyle}>
            <div style={sectionTitleStyles}>
              <FactCheckOutlinedIcon sx={{ fontSize: 18, color: '#10b981' }} />
              CAPE Phase II — Claim Eligibility
            </div>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                margin: '0 0 12px 0',
                lineHeight: '1.5',
              }}
            >
              Phase I asks <em>“is this a valid IEEPA row?”</em>. Phase II is
              claim-centric — it asks{' '}
              <strong style={{ color: 'var(--text-primary-color)' }}>
                “can this entry go into a legally supportable refund claim, for
                the correct amount, without duplicate or unsupported recovery?”
              </strong>{' '}
              Every line that clears the Phase I gate is additionally classified{' '}
              <strong style={{ color: 'var(--text-primary-color)' }}>
                Claimable
              </strong>{' '}
              or{' '}
              <strong style={{ color: 'var(--text-primary-color)' }}>
                Needs Review
              </strong>
              . Needs-Review lines are still included in the report (flagged, not
              dropped) so a preparer can decide what to keep.
            </p>
            <p style={{ ...listStyles, marginLeft: 0, marginBottom: '8px' }}>
              A line is flagged <strong style={labelStyles}>Needs Review</strong>{' '}
              when it carries:
            </p>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Mixed Chapter 99:</strong> refundable
                IEEPA plus non-refundable programs (e.g. Section 232 / 301) on
                the same line.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>AD / CVD duties:</strong> excluded
                from the refund amount, but flagged for independent
                reconciliation.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Post-entry activity:</strong> a prior
                Post-Summary Correction, protest, or reliquidation on the entry.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Refund anomalies:</strong> a
                zero-dollar, over-recovery, or unusually large refund, or an
                amount-dependent rate that needs a manual calculation.
              </li>
              <li style={listItemStyles}>
                <strong style={labelStyles}>Possible override:</strong> entered
                duty that differs from BITE’s expected duty.
              </li>
            </ul>

            <p
              style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                margin: '12px 0 0 0',
                lineHeight: '1.5',
              }}
            >
              An <strong style={labelStyles}>Entry Rollup</strong> report is also
              available — one row per entry summary (the claim unit) with line
              counts by status, total IEEPA duty and estimated refund, AD/CVD
              totals kept separate, duplicate-line detection, and entry-level
              refund reconciliation.
            </p>

            {/* Tier-1 disclosures: not answerable from ES-003 alone. */}
            <div
              style={{
                marginTop: '16px',
                borderLeft: '3px solid #f79009',
                background: 'var(--bg-colors)',
                borderRadius: '6px',
                padding: '12px 14px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-primary-color)',
                  marginBottom: '8px',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 16, color: '#f79009' }} />
                Cannot be determined from ES-003 data alone
              </div>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 8px 0',
                  lineHeight: '1.5',
                }}
              >
                These checks require records the ES-003 export does not contain.
                They appear in a{' '}
                <strong style={labelStyles}>Verification Gaps</strong> column on
                every Phase II row — confirm them against your own records before
                submitting:
              </p>
              <ul style={{ ...listStyles, marginBottom: 0 }}>
                <li style={listItemStyles}>
                  Whether the entry was already included in a prior CAPE
                  submission or has already received an IEEPA refund.
                </li>
                <li style={listItemStyles}>
                  Supporting documentation — it is not part of the ES-003 file.
                </li>
                <li style={listItemStyles}>
                  AD/CVD case status — administrative reviews, scope inquiries, or
                  changed-circumstances reviews.
                </li>
                <li style={listItemStyles}>
                  Quota / tariff-rate-quota (TRQ) identification, and Section 201
                  safeguard vs other Chapter 99 detail.
                </li>
              </ul>
            </div>
          </div>

          {/* Section: Generating the IEEPA Report */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ ...sectionTitleStyles, marginBottom: 0 }}>
              <FileDownloadOutlinedIcon
                sx={{ fontSize: 18, color: '#10b981' }}
              />
              Generating Your Report
            </div>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                margin: 0,
                lineHeight: '1.5',
              }}
            >
              Once your ACE document containing all required synthesized columns
              is uploaded and an analysis is completed, you can view the
              results. If you have all of those columns you can run an ACE
              Analysis and then click the Generate IEEPA Report button on the
              top right of their results table to have all of their transactions
              that apply for Phase 1 of the IEEPA returns displayed for them.
              This button is located in the toolbar of the analysis details page
              and will export a CSV of all transactions applicable for Phase 1
              IEEPA returns.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--es-soft-border)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            className='btn-outline'
            onClick={handleClose}
            style={{ margin: 0, width: 'auto' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IeepaInfoModal;
